import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { CheckCircle, XCircle, AlertCircle, RefreshCw, Copy, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import { supabase } from '../lib/supabase';
import { StockHeroStrip, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';

interface TestResult {
  name: string;
  status: 'pending' | 'running' | 'pass' | 'fail';
  message?: string;
  details?: any;
}

export default function AuthDiagnostic() {
  const [email, setEmail] = useState('test@example.com');
  const [password, setPassword] = useState('test123456');
  const [name, setName] = useState('Test User');
  const [results, setResults] = useState<TestResult[]>([]);
  const [running, setRunning] = useState(false);
  const navigate = useNavigate();

  const updateResult = (index: number, updates: Partial<TestResult>) => {
    setResults(prev => prev.map((r, i) => i === index ? { ...r, ...updates } : r));
  };

  const runDiagnostics = async () => {
    setRunning(true);
    const tests: TestResult[] = [
      { name: '1. Backend Health Check', status: 'pending' },
      { name: '2. Supabase Connection', status: 'pending' },
      { name: '3. Check User Exists', status: 'pending' },
      { name: '4. Sign Up (Create User)', status: 'pending' },
      { name: '5. Sign In (Get Session)', status: 'pending' },
      { name: '6. Session Validation', status: 'pending' },
    ];
    setResults(tests);

    // TEST 1: Backend Health
    let testIndex = 0;
    updateResult(testIndex, { status: 'running' });
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/health`,
        {
          headers: { 'Authorization': `Bearer ${publicAnonKey}` }
        }
      );
      const data = await response.json();
      
      if (response.ok && data.status) {
        updateResult(testIndex, { 
          status: 'pass', 
          message: `Backend: ${data.status}`,
          details: data 
        });
      } else {
        throw new Error('Backend unhealthy');
      }
    } catch (error: any) {
      updateResult(testIndex, { 
        status: 'fail', 
        message: `Backend offline: ${error.message}`,
        details: error
      });
      setRunning(false);
      return; // Stop if backend is down
    }

    // TEST 2: Supabase Connection
    testIndex = 1;
    updateResult(testIndex, { status: 'running' });
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      updateResult(testIndex, { 
        status: 'pass', 
        message: 'Supabase client working',
        details: { hasSession: !!data.session }
      });
    } catch (error: any) {
      updateResult(testIndex, { 
        status: 'fail', 
        message: error.message,
        details: error
      });
    }

    // TEST 3: Check if user exists
    testIndex = 2;
    updateResult(testIndex, { status: 'running' });
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/check-user-exists`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${publicAnonKey}`,
          },
          body: JSON.stringify({ email }),
        }
      );
      const data = await response.json();
      
      updateResult(testIndex, { 
        status: 'pass', 
        message: data.exists ? 'User exists (will skip signup)' : 'User does not exist (will create)',
        details: data
      });

      // If user exists, skip signup and go straight to signin
      if (data.exists) {
        setResults(prev => prev.map((r, i) => 
          i === 3 ? { ...r, status: 'pass', message: 'Skipped (user exists)' } : r
        ));
        testIndex = 4; // Jump to signin
      } else {
        // TEST 4: Sign Up
        testIndex = 3;
      }
    } catch (error: any) {
      updateResult(testIndex, { 
        status: 'fail', 
        message: error.message,
        details: error
      });
    }

    // TEST 4: Sign Up (only if user doesn't exist)
    if (testIndex === 3) {
      updateResult(testIndex, { status: 'running' });
      try {
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/signup`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${publicAnonKey}`,
            },
            body: JSON.stringify({ email, password, name }),
          }
        );
        
        const data = await response.json();
        console.log('Signup response:', data);
        
        if (!response.ok) {
          throw new Error(data.error || 'Signup failed');
        }
        
        updateResult(testIndex, { 
          status: 'pass', 
          message: 'User created successfully',
          details: data
        });
      } catch (error: any) {
        updateResult(testIndex, { 
          status: 'fail', 
          message: error.message,
          details: error
        });
        
        // If signup failed, check if it's because user exists
        if (error.message.includes('already exists') || error.message.includes('USER_ALREADY_EXISTS')) {
          updateResult(testIndex, { 
            status: 'pass', 
            message: 'User already exists (proceeding to sign in)',
            details: error
          });
        } else {
          setRunning(false);
          return; // Stop on other signup errors
        }
      }
    }

    // TEST 5: Sign In
    testIndex = 4;
    updateResult(testIndex, { status: 'running' });
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/signin`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${publicAnonKey}`,
          },
          body: JSON.stringify({ email, password }),
        }
      );
      
      const data = await response.json();
      console.log('Signin response:', data);
      
      if (!response.ok) {
        throw new Error(data.error || 'Sign in failed');
      }
      
      if (!data.session || !data.session.access_token) {
        throw new Error('No access token received');
      }
      
      updateResult(testIndex, { 
        status: 'pass', 
        message: `Signed in! Token: ${data.session.access_token.substring(0, 20)}...`,
        details: {
          user: data.user,
          hasSession: !!data.session,
          hasAccessToken: !!data.session.access_token
        }
      });

      // Store token for next test
      localStorage.setItem('diagnostic_token', data.session.access_token);
    } catch (error: any) {
      updateResult(testIndex, { 
        status: 'fail', 
        message: error.message,
        details: error
      });
      setRunning(false);
      return;
    }

    // TEST 6: Session Validation
    testIndex = 5;
    updateResult(testIndex, { status: 'running' });
    try {
      const token = localStorage.getItem('diagnostic_token');
      if (!token) {
        throw new Error('No token found');
      }

      // Decode token to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresAt = new Date(payload.exp * 1000);
      const isExpired = payload.exp < Date.now() / 1000;
      
      if (isExpired) {
        throw new Error('Token is expired');
      }
      
      updateResult(testIndex, { 
        status: 'pass', 
        message: `Token valid until ${expiresAt.toLocaleString()}`,
        details: {
          userId: payload.sub,
          expiresAt: expiresAt.toISOString(),
          isExpired
        }
      });
    } catch (error: any) {
      updateResult(testIndex, { 
        status: 'fail', 
        message: error.message,
        details: error
      });
    }

    setRunning(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pass':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'fail':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'running':
        return <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />;
      default:
        return <div className="w-5 h-5 rounded-full border-2 border-neutral-300" />;
    }
  };

  return (
    <WarmMarketingBackdrop>
    <div className="min-h-screen p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <StockHeroStrip stockKey="softAbstract" alt="Decorative abstract imagery" />
        <div className="space-y-2 text-center">
          <h1 className="text-3xl font-bold">🔍 TENSION Auth Diagnostic</h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            Complete authentication system test
          </p>
        </div>

        <Card className="p-6 space-y-4">
          <h2 className="text-xl font-semibold">Test Credentials</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium">Email</label>
              <Input 
                value={email} 
                onChange={(e) => setEmail(e.target.value)}
                disabled={running}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Password</label>
              <Input 
                type="password"
                value={password} 
                onChange={(e) => setPassword(e.target.value)}
                disabled={running}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Name</label>
              <Input 
                value={name} 
                onChange={(e) => setName(e.target.value)}
                disabled={running}
              />
            </div>
          </div>

          <Button 
            onClick={runDiagnostics} 
            disabled={running}
            className="w-full"
          >
            {running ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Running Tests...
              </>
            ) : (
              'Run Complete Diagnostic'
            )}
          </Button>
        </Card>

        {results.length > 0 && (
          <Card className="p-6 space-y-4">
            <h2 className="text-xl font-semibold">Test Results</h2>
            <div className="space-y-3">
              {results.map((result, index) => (
                <div 
                  key={index}
                  className={`tension-glow p-4 rounded-lg border ${
                    result.status === 'pass' 
                      ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' 
                      : result.status === 'fail'
                      ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'
                      : 'bg-neutral-50 dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {getStatusIcon(result.status)}
                    <div className="flex-1">
                      <h3 className="font-medium">{result.name}</h3>
                      {result.message && (
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                          {result.message}
                        </p>
                      )}
                      {result.details && (
                        <details className="mt-2">
                          <summary className="text-xs text-neutral-500 cursor-pointer hover:text-neutral-700 dark:hover:text-neutral-300">
                            Show details
                          </summary>
                          <pre className="mt-2 text-xs bg-neutral-900 dark:bg-neutral-950 text-green-400 p-2 rounded overflow-auto">
                            {JSON.stringify(result.details, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <Card className="p-6 bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-900 dark:text-amber-100">Important Notes</h3>
              <ul className="mt-2 space-y-1 text-sm text-amber-800 dark:text-amber-200">
                <li>• Backend must be deployed to Supabase for auth to work</li>
                <li>• Check browser console for detailed error logs</li>
                <li>• Test user will be created/updated with credentials above</li>
                <li>• All tests must pass for auth to work in the app</li>
              </ul>
            </div>
          </div>
        </Card>

        <div className="text-center text-sm text-neutral-500">
          <p>Project ID: {projectId}</p>
          <p>Backend: https://{projectId}.supabase.co/functions/v1/make-server-ba6cedc4</p>
        </div>
      </div>
    </div>
    </WarmMarketingBackdrop>
  );
}